import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import AppShell from '../../components/layout/AppShell';
import DeviceDrivenInspectionChecklist from '../../components/inspection/DeviceDrivenInspectionChecklist';
import { apiRequest } from '../../api/client';
import InspectionChecklistLegacy from './InspectionChecklistLegacy';
import '../admin/quotes/quote-generator.css';

/**
 * Technician inspection runner. New inspections (`deviceDriven`) use device-registry workflow;
 * legacy template inspections retain the historical checklist UX.
 */
export default function InspectionChecklist(): React.JSX.Element {
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const backPath = (location.state as { backPath?: string } | null)?.backPath;
    const [mode, setMode] = useState<'loading' | 'device' | 'legacy'>('loading');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const detect = useCallback(async (idParam: string) => {
        setErrorMessage(null);
        try {
            const res = await apiRequest<{ data: { deviceDriven?: boolean } }>(`/api/inspections/${idParam}`);
            const flag = !!(res.data && (res.data as { deviceDriven?: boolean }).deviceDriven);
            setMode(flag ? 'device' : 'legacy');
        } catch (e: unknown) {
            setErrorMessage(e instanceof Error ? e.message : 'Could not resolve checklist mode.');
            setMode('legacy');
        }
    }, []);

    useEffect(() => {
        if (!id) {
            setErrorMessage('Missing inspection route.');
            setMode('legacy');
            return;
        }
        setMode('loading');
        detect(id).catch(() => undefined);
    }, [detect, id]);

    if (mode === 'loading') {
        return (
            <AppShell>
                <div className="quote-page quote-container">
                    <p style={{ color: 'var(--dash-muted)' }}>Opening checklist…</p>
                    {errorMessage ? <div className="banner banner-error">{errorMessage}</div> : null}
                </div>
            </AppShell>
        );
    }

    if (mode === 'device') {
        return (
            <DeviceDrivenInspectionChecklist
                basePath="/technician/inspections"
                backPath={backPath}
            />
        );
    }

    return <InspectionChecklistLegacy />;
}
